import { GetPlayerProfileResponse } from 'services/players';
import dynamic from 'next/dynamic';
import styles from './profile-layout.module.scss';
import { Tabs } from 'core/generic/components/general/tabs';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { Bio } from '@players/components/bio/bio';
import InfoAndSocialsSection, {
    PlayerSocial,
} from '@players/components/info-and-socials-section/info-and-socials-section';
import { Honours } from '@players/components/honours/honours';
import { OtherPlayersSection } from '@players/components/other-players-section/other-players-section';
import { NewsList } from '@players/components/news-list/news-list';
import { useAppTranslation } from 'hooks/use-translates';

const HeroPlayer = dynamic(() => import('core/players/components/hero-player'), { ssr: false });

type ProfileLayoutProps = {
    data?: GetPlayerProfileResponse;
};

const getPlayerSocials = (data: GetPlayerProfileResponse): PlayerSocial[] => {
    return [
        data.twitterUsername
            ? { type: 'Twitter/X', username: data.twitterUsername, hide: data.hideTwitter }
            : null,
        data.instagramUsername
            ? { type: 'Instagram', username: data.instagramUsername, hide: data.hideInstagram }
            : null,
        data.twitchUsername ? { type: 'Twitch', username: data.twitchUsername } : null,
        data.tiktokUsername ? { type: 'TikTok', username: data.tiktokUsername } : null,
    ].filter(Boolean) as PlayerSocial[];
};

export const ProfileLayout = ({ data }: ProfileLayoutProps) => {
    const router = useRouter();
    const [activeTab, setActiveTab] = useState(0);
    const { t } = useAppTranslation();
    const firstTeamLayout =
        (data?.isMensTeam || data?.isWomensTeam || data?.team === 'esports') && !data?.isManager;
    useEffect(() => {
        if (!router.isReady) return;

        const pathSegments = router.asPath.split('?')[0].split('/').filter(Boolean);

        if (pathSegments.includes('news')) {
            setActiveTab(1);
        } else {
            setActiveTab(0);
        }
    }, [router.asPath, router.isReady]);

    const handleTabChange = (index: number) => {
        if (typeof window !== 'undefined') {
            const currentPath = window.location.pathname.replace('/news', '');
            if (index === 1) {
                window.history.replaceState(null, '', `${currentPath}/news`);
            } else {
                window.history.replaceState(null, '', `${currentPath}`);
            }
            setActiveTab(index);
        }
    };

    const socials = data ? getPlayerSocials(data) : [];

    return (
        <div className={styles['profile-page']}>
            {data && (
                <>
                    <HeroPlayer
                        isManager={data.isManager}
                        isEsports={data.team === 'esports'}
                        firstName={data.firstName}
                        lastName={data.lastName}
                        number={data.shirtNumber}
                        country={data.country}
                        flagUri={data.flagUri}
                        position={data.position}
                        dateOfBirth={data.dateOfBirth}
                        halfBodyImageUri={data.halfBodyImageUri}
                        isWomensTeam={data.isWomensTeam}
                    />
                    <Tabs activeItem={activeTab}>
                        {firstTeamLayout && (
                            <Tabs.TabList
                                onChange={handleTabChange}
                                tabType="Underlined"
                                className={`container ${styles['tabs-wrapper']}`}
                            >
                                <Tabs.Tab
                                    index={0}
                                    label={t(
                                        'players_profile.sub_header:profile_label',
                                        undefined,
                                        'Profile',
                                    )}
                                ></Tabs.Tab>
                                {data.news.itemCount > 0 && (
                                    <Tabs.Tab
                                        index={1}
                                        label={t(
                                            'players_profile.news_section_title',
                                            undefined,
                                            'News & videos',
                                        )}
                                    ></Tabs.Tab>
                                )}
                            </Tabs.TabList>
                        )}
                        <Tabs.Panel index={0}>
                            <div className={styles['tab-panel-wrapper']}>
                                <div className="container">
                                    <div className={`row ${styles['profile-content']}`}>
                                        <div
                                            className={`col-12 col-lg-7 ${styles['profile-content__bio-section']}`}
                                        >
                                            <Bio content={data.bio} />
                                        </div>
                                        <div
                                            className={`col-12 col-lg-3 col-offset-lg-9 ${styles['profile-content__info-socials']}`}
                                        >
                                            <InfoAndSocialsSection
                                                dateOfBirth={data.dateOfBirth}
                                                birthCity={data.birthCity}
                                                birthCountry={data.birthCountry}
                                                dateJoined={data.dateJoined}
                                                socials={socials}
                                            />
                                        </div>
                                        {data.awards.length > 0 && (
                                            <div
                                                className={`col-12 col-lg-7 ${styles['profile-content__honours']}`}
                                            >
                                                <Honours awards={data.awards} name={data.name} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Tabs.Panel>
                        {data.news.itemCount > 0 && firstTeamLayout && (
                            <Tabs.Panel index={1}>
                                <div className={styles['tab-panel-wrapper']}>
                                    <NewsList
                                        firstName={data.firstName}
                                        lastName={data.lastName}
                                        news={data.news.items}
                                    />
                                </div>
                            </Tabs.Panel>
                        )}
                    </Tabs>
                    {data.playersInPosition?.length > 0 &&
                        (data.isMensTeam || data.isWomensTeam || data.team === 'esports') && (
                            <OtherPlayersSection
                                position={data.positionKey}
                                isEsportsTeam={data.team === 'esports'}
                                players={data.playersInPosition}
                            />
                        )}
                </>
            )}
        </div>
    );
};
